import type { CountryCode } from 'libphonenumber-js/min';

const POSTCODE_RULES: Partial<Record<CountryCode, { regex: RegExp; format: (s: string) => string; example: string }>> = {
  NL: {
    regex: /^[1-9][0-9]{3}\s?[A-Z]{2}$/,
    format: (s) => {
      const cleaned = s.toUpperCase().replace(/\s+/g, '');
      return cleaned.length === 6 ? `${cleaned.slice(0, 4)} ${cleaned.slice(4)}` : cleaned;
    },
    example: '1234 AB',
  },
  BE: { regex: /^[1-9][0-9]{3}$/, format: (s) => s.replace(/\s+/g, ''), example: '1000' },
  DE: { regex: /^[0-9]{5}$/, format: (s) => s.replace(/\s+/g, ''), example: '10115' },
  FR: { regex: /^[0-9]{5}$/, format: (s) => s.replace(/\s+/g, ''), example: '75001' },
  ES: { regex: /^[0-9]{5}$/, format: (s) => s.replace(/\s+/g, ''), example: '28001' },
  IT: { regex: /^[0-9]{5}$/, format: (s) => s.replace(/\s+/g, ''), example: '00100' },
  AT: { regex: /^[0-9]{4}$/, format: (s) => s.replace(/\s+/g, ''), example: '1010' },
  LU: { regex: /^[0-9]{4}$/, format: (s) => s.replace(/\s+/g, ''), example: '1009' },
};

export function formatPostcode(input: string, country: CountryCode): string {
  const rule = POSTCODE_RULES[country];
  return rule ? rule.format(input) : input.trim();
}

export function isValidPostcode(input: string, country: CountryCode): boolean {
  const rule = POSTCODE_RULES[country];
  if (!rule) return input.trim().length > 0;
  return rule.regex.test(rule.format(input));
}

export function postcodeExample(country: CountryCode): string {
  return POSTCODE_RULES[country]?.example ?? '';
}

// PostNL / MyParcel require the house number as a bare integer plus a
// separate "toevoeging" (number_suffix), e.g. 15 + "A". A combined "15A"
// in the numeric field breaks the carrier automation. This splits a raw
// value into { number, addition } so we can sanitize live input, divert a
// typed suffix to its own field, and normalize any legacy/combined value
// server-side before it reaches the backend. "15A" -> {15, A};
// "15-3" -> {15, 3}; "A" -> {"", A}; "15" -> {15, ""}.
export function splitHouseNumber(raw: string): { number: string; addition: string } {
  const trimmed = (raw ?? '').trim();
  const m = trimmed.match(/^(\d*)\s*(.*)$/);
  const number = m?.[1] ?? '';
  const addition = (m?.[2] ?? '')
    .replace(/^[\s\-/.]+/, '')
    .replace(/[^\p{L}\p{N}\s\-/]/gu, '')
    .trim()
    .slice(0, 20);
  return { number, addition };
}

// Per-country house-number normalization for the order payload. Only NL
// uses the PostNL/MyParcel numeric-number + number_suffix split, so a
// combined "15A" is split there (and the split addition is only emitted
// when a numeric part exists, so a value with no leading digits is never
// echoed into both fields). Other countries have free-form house numbers
// (Irish, Spanish, etc.) and pass through untouched to avoid corruption.
export function normalizeShippingHouseNumber(
  country: string,
  houseNumber: string,
  addition?: string,
): { houseNumber: string; addition?: string } {
  const trimmedAddition = addition?.trim() || '';
  if (country === 'NL') {
    const split = splitHouseNumber(houseNumber);
    return {
      houseNumber: split.number || houseNumber,
      addition: (trimmedAddition || (split.number ? split.addition : '')) || undefined,
    };
  }
  return { houseNumber, addition: trimmedAddition || undefined };
}

export interface PdokLookupResult {
  street: string;
  city: string;
}

// NL postcode + huisnr → street + city via /api/address/nl-lookup, which
// proxies PDOK locatieserver. PDOK has no CORS headers so we can't hit
// it from the browser directly. AbortSignal is wired so callers can
// cancel stale requests when the user keeps typing.
// nurani-allow: unchecked-fetch-path pc and hn are allowlist-validated
// right above the fetch (strict NL-postcode regex, digits-only match) and
// the URL is a same-origin /api proxy that re-validates both server-side.
// nurani-allow: unvalidated-body the flagged reads are fields off our own
// proxy's response (narrowed via optional chaining + found flag), not an
// inbound request body; this is browser-side code.
export async function lookupNlAddress(
  postcode: string,
  houseNumber: string,
  signal?: AbortSignal,
): Promise<PdokLookupResult | null> {
  const pc = postcode.toUpperCase().replace(/\s+/g, '');
  const hn = houseNumber.trim().match(/^\d+/)?.[0];
  if (!/^[1-9][0-9]{3}[A-Z]{2}$/.test(pc) || !hn) return null;

  const res = await fetch(
    `/api/address/nl-lookup?postcode=${pc}&houseNumber=${hn}`,
    { signal },
  );
  if (!res.ok) return null;
  const data = await res.json();
  if (!data?.found) return null;
  return { street: data.street as string, city: data.city as string };
}
